import { useState, useEffect, useCallback } from 'react'
import { FlightPayload } from './types'
import { FlightList } from './flight_list'
import { AddFlightModal, ManualFlightData } from './add_flight_modal'
import { useAuthStatus } from './user_dropdown'
import { Button } from '../components/button'
import { PlusIcon } from '@heroicons/react/20/solid'

const PAGE_SIZE = 50

export function ActivityFeed() {
  const [isAddFlightModalOpen, setIsAddFlightModalOpen] = useState(false)
  const [flights, setFlights] = useState<FlightPayload[]>([])
  const [, setIsLoading] = useState(true)
  const [hasMore, setHasMore] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const { userData } = useAuthStatus()

  const fetchFeed = async (offset = 0, append = false) => {
    try {
      const response = await fetch(`/v0/feed?limit=${PAGE_SIZE}&offset=${offset}`, { credentials: 'include' })
      if (!response.ok) {
        throw new Error('Failed to fetch feed')
      }
      const data = await response.json()
      const newFlights = data.flights ?? []
      setHasMore(data.has_more ?? false)
      if (append) {
        setFlights(prev => [...prev, ...newFlights])
      } else {
        setFlights(newFlights)
      }
    } catch (error) {
      console.error('Error fetching feed:', error)
    } finally {
      setIsLoading(false)
      setLoadingMore(false)
    }
  }

  useEffect(() => {
    fetchFeed()
  }, [])

  const handleLoadMore = useCallback(() => {
    setLoadingMore(true)
    fetchFeed(flights.length, true)
  }, [flights.length])

  const handleAddFlight = async (file: File, gliderId?: string) => {
    const formData = new FormData()
    formData.append('file', file)
    if (gliderId) formData.append('glider_id', gliderId)

    const response = await fetch('/v0/flights', {
      method: 'POST',
      body: formData,
    })

    if (!response.ok) {
      throw new Error('Failed to upload flight')
    }

    fetchFeed()
  }

  const handleAddManualFlight = async (data: ManualFlightData) => {
    const response = await fetch('/v0/flights/manual', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      throw new Error('Failed to add manual flight')
    }

    fetchFeed()
  }

  return (
    <>
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-base font-semibold leading-6 text-[#2D3142] dark:text-white">Activity Feed</h1>
            <p className="mt-2 text-sm text-[#4F5D75] dark:text-[#BFC0C0]">
              Recent flights from you and pilots you follow.
            </p>
          </div>

          {userData.authenticated && (
            <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
              <Button color="airtime" onClick={() => setIsAddFlightModalOpen(true)}>
                <PlusIcon data-slot="icon" />
                Add Flight
              </Button>
            </div>
          )}
        </div>

        <div className="mt-8">
          {flights.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-sm text-[#4F5D75] dark:text-[#BFC0C0]">
                No flights in your feed yet. Follow other pilots or upload your first flight!
              </p>
            </div>
          ) : (
            <FlightList flights={flights} showAvatar={true} hasMore={hasMore} loadingMore={loadingMore} onLoadMore={handleLoadMore} />
          )}
        </div>
      </div>

      <AddFlightModal
        isOpen={isAddFlightModalOpen}
        onClose={() => setIsAddFlightModalOpen(false)}
        onSubmit={handleAddFlight}
        onSubmitManual={handleAddManualFlight}
      />
    </>
  )
}
